import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardFooter,
  Container,
  Row,
  Col,
  Button,
  Input,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import Pagination from "components/shared/Pagination";
import AddDriverModal from "components/Modals/AddDriverModal";
import EditDriverModal from "components/Modals/EditDriverModal";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import OnlyHeader from "components/Layout/OnlyHeader";
import { addNewUser } from "store/actions/authActions";
import { updateDriver } from "store/actions/driverAction";
import { deleteDriver } from "store/actions/driverAction";
import { getAllDrivers } from "store/actions/driverAction";
import TableComponent from "components/shared/Table";
import DebouncedSearch from "components/shared/DebouncedSearch";
import { validatePassword } from "utils/ValidatePassword";
import { validateEmail } from "utils/validateEmail";

const Drivers = () => {
  const dispatch = useDispatch();
  const {
    driverData,
    driverRestData,
    getAllLoading,
    updateLoading,
    deleteLoading,
  } = useSelector((state) => state.driver);

  const { loading } = useSelector((state) => state.auth);
  const { uid, user } = useSelector((state) => state.auth);
  const [hitsPerPage, setHitsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [errors, setErrors] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [id, setId] = useState("");

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const addtoggle = () => {
    setAddModal((prev) => !prev);
    setFormData({ name: "", lastName: "", email: "", password: "" });
  };
  const editToggle = () => setEditModal((prev) => !prev);
  const deletetoggle = () => setDeleteModal((prev) => !prev);
  const handleSearch = (debouncedValue) => {
    setDebouncedSearch(debouncedValue);
  };

  const getDriverStatus = (driver) => {
    if (!driver?.isAssigned) return "Unassigned";

    if (!driver?.lastAssignedAt?._seconds) return "Unassigned";

    const lastAssignedTime = new Date(driver?.lastAssignedAt._seconds * 1000);
    const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);

    return lastAssignedTime > twoHoursAgo ? "Assigned" : "Unassigned";
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name,
      email: item.email,
    });
    setId(item.id || item._id);
    editToggle();
  };

  const handleUpdateDriver = (event) => {
    event.preventDefault();
    const updatedFields = {
      name: formData.name,
    };
    dispatch(
      updateDriver(id, updatedFields, () => {
        toast.success("Driver Name Updated Successfully");
        editToggle();
      })
    );
  };

  const handleDeleteDriver = () => {
    dispatch(
      deleteDriver(id, () => {
        setTimeout(() => {
          dispatch(
            getAllDrivers(
              user?.restaurantID,
              hitsPerPage,
              debouncedSearch,
              currentPage
            )
          );
        }, 4000);
        deletetoggle();
        setCurrentPage(0);
      })
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const email = formData?.email;
    if (email) {
      const emailError = validateEmail(formData?.email);
      if (emailError) {
        toast.error(emailError);
        return;
      }
    }
    if (formData?.password) {
      const validationErrors = validatePassword(formData.password);
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        return;
      } else {
        setErrors("");
      }
    }
    const obj = {
      name: formData.name,
      email: formData.email,
      password: formData.password,
      type: "driver",
      isAssigned: false,
      restaurantID: uid,
    };

    dispatch(
      addNewUser(obj, [], () => {
        addtoggle();
        setCurrentPage(0);
        setTimeout(() => {
          dispatch(
            getAllDrivers(
              user?.restaurantID,
              hitsPerPage,
              debouncedSearch,
              currentPage
            )
          );
        }, 4000);
      })
    );
  };

  useEffect(() => {
    dispatch(
      getAllDrivers(
        user?.restaurantID,
        hitsPerPage,
        debouncedSearch,
        currentPage
      )
    ).finally(() => setInitialLoad(false));
  }, [dispatch, user?.restaurantID, hitsPerPage, debouncedSearch, currentPage]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch]);

  return (
    <>
      <OnlyHeader />
      <Container className="mt--7 mb-5" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader>
                <Row>
                  <Col xs="12" lg="4">
                    <h3 className="pt-2 mr-2">Drivers</h3>
                  </Col>
                  <Col xs="12" lg="3">
                    <DebouncedSearch
                      placeholder="Search by name or email"
                      onSearch={handleSearch}
                    />
                  </Col>
                  <Col xs="12" lg="3" className="my-2 my-lg-0">
                    <Input
                      type="select"
                      id="exampleCustomSelect"
                      name="customSelect"
                      value={hitsPerPage}
                      onChange={(e) => {
                        setHitsPerPage(e.target.value);
                      }}
                    >
                      <option value="5">Show Entries</option>
                      <option value="10">10</option>
                      <option value="15">15</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </Input>
                  </Col>
                  <Col xs="12" lg="2">
                    <Button color="primary" block onClick={addtoggle}>
                      Add
                    </Button>
                  </Col>
                </Row>
              </CardHeader>

              {initialLoad && getAllLoading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spinner size="md" color="primary" />
                </div>
              ) : (
                <>
                  <TableComponent
                    className="align-items-center table-flush"
                    responsive
                  >
                    {driverData?.length === 0 ? (
                      <tbody>
                        <tr>
                          <td className="text-center py-5" colSpan="5">
                            No Drivers to display
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      <>
                        <thead className="thead-light">
                          <tr>
                            <th scope="col">Full Name</th>
                            <th scope="col">Email</th>
                            <th scope="col">Verified</th>
                            <th scope="col">Status</th>
                            <th scope="col">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(driverData) &&
                            driverData?.map((item, index) => (
                              <tr key={index}>
                                <td className="text-capitalize">
                                  {item?.name}
                                </td>
                                <td>{item?.email}</td>
                                <td>{item?.isVerified ? "Yes" : "No"}</td>
                                <td>{getDriverStatus(item)}</td>
                                <td>
                                  <Button
                                    className="btn-sm"
                                    color="primary"
                                    onClick={() => handleEdit(item)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    className="btn-sm"
                                    color="danger"
                                    onClick={() => {
                                      setId(item?.id);
                                      deletetoggle();
                                    }}
                                  >
                                    Delete
                                  </Button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </>
                    )}
                  </TableComponent>
                </>
              )}

              <CardFooter>
                <Pagination
                  currentPage={currentPage}
                  totalPages={driverRestData?.nbPages || 0}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </CardFooter>
            </Card>
          </div>
        </Row>

        <div>
          <AddDriverModal
            isOpen={addModal}
            toggle={addtoggle}
            formData={formData}
            errors={errors}
            handleInputChange={handleInputChange}
            handleSubmit={handleSubmit}
            loading={loading}
          />
          <EditDriverModal
            isOpen={editModal}
            toggle={editToggle}
            formData={formData}
            handleInputChange={handleInputChange}
            handleUpdateDriver={handleUpdateDriver}
            isLoading={updateLoading}
          />

          <ConfirmationModal
            title="Delete Driver"
            text="Are you sure you want to delete driver"
            isOpen={deleteModal}
            toggle={deletetoggle}
            isLoading={deleteLoading}
            onConfirmation={handleDeleteDriver}
          />
        </div>
      </Container>
    </>
  );
};

export default Drivers;
